/**
 * Capabilities a host app passes into a package's config provider.
 *
 * Packages never read the host's permission store or currency settings
 * directly; each package's config (`CatalogConfigProvider`, the
 * container-orders provider) carries these optional fields instead, and
 * `resolveHostCapabilities` fills in the fallbacks.
 */
import { useMemo } from 'react';

/** True when the current user holds `permission`. */
export type CanFn = (permission: string) => boolean;

/** Formats a money amount; `currency` is an ISO 4217 code when known. */
export type FormatCurrencyFn = (
  amount: string | number | null | undefined,
  currency?: string,
) => string;

export interface HostCapabilities {
  /** Optional. When missing, permission-gated actions are hidden. */
  can?: CanFn;
  /** Optional. Falls back to `defaultFormatCurrency`. */
  formatCurrency?: FormatCurrencyFn;
}

export interface ResolvedHostCapabilities {
  can: CanFn;
  formatCurrency: FormatCurrencyFn;
}

/** Without a host `can`, nothing gated is allowed. */
export const denyAll: CanFn = () => false;

/**
 * A plain `Intl.NumberFormat` fallback. Blank or non-numeric amounts render
 * as an em dash; an unknown currency code falls back to a plain number.
 */
export const defaultFormatCurrency: FormatCurrencyFn = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const value = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
};

export function resolveHostCapabilities(
  capabilities: HostCapabilities | null | undefined,
): ResolvedHostCapabilities {
  return {
    can: capabilities?.can ?? denyAll,
    formatCurrency: capabilities?.formatCurrency ?? defaultFormatCurrency,
  };
}

/** `resolveHostCapabilities`, memoised on the two functions. */
export function useHostCapabilities(
  capabilities: HostCapabilities | null | undefined,
): ResolvedHostCapabilities {
  const can = capabilities?.can;
  const formatCurrency = capabilities?.formatCurrency;
  return useMemo(
    () => resolveHostCapabilities({ can, formatCurrency }),
    [can, formatCurrency],
  );
}
